import { floor } from 'lodash'

import Card from '@/components/Card'
import { TENANTS } from '@/lib/tenants/constants'
import { Tenants } from '@/lib/tenants/types'
import { fetchListingBySimilar } from '@/page-commons/listingBysimilarQuery'
import { fetchPrioritiesBySimilar } from '@/page-commons/prioritiesBySimilar'
import {
  ListingsBySimilarQueryVariables,
  Listings_Detail,
  Potential_Property,
} from '@/types/cate-graphql-generated-types'
import { CardData } from '@/types/property-card'

import { mapListingsIntoCardDataListing, mapPrioritiesIntoCardDataPriority } from './property-card'

export const MAX_SIMILAR_LISTINGS = 7

type Range = {
  area: number
  price: number
}

type DeltaPriceInfo = {
  isPrivateNegotiation: boolean
  sellingPriceCents: number
}

type SimilarCriteria = {
  city?: string
  commercialArea: number
  id: string
  isPrivateNegotiation: boolean
  priceInCents: number
  rooms: number
  zoneSlug?: string
}

type QueryRangeParams = {
  commercialArea: number
  isPrivateNegotiation: boolean
  priceInCents: number
}

const getQueryRange = ({ commercialArea, isPrivateNegotiation, priceInCents }: QueryRangeParams): Range => ({
  area: floor(commercialArea * 0.25),
  price: isPrivateNegotiation ? 0 : floor(priceInCents * 0.25),
})

export const similarRanges = (
  similarCriteria: SimilarCriteria,
  geoSlugsIncludeCity: boolean
): ListingsBySimilarQueryVariables => {
  const { city, commercialArea, isPrivateNegotiation, priceInCents, rooms, zoneSlug } = similarCriteria
  const { area: areaRange, price: priceRange } = getQueryRange({ commercialArea, isPrivateNegotiation, priceInCents })
  return {
    areaMax: commercialArea + areaRange,
    areaMin: commercialArea - areaRange,
    geoSlugs: geoSlugsIncludeCity ? [city] : [zoneSlug || city],
    priceMax: isPrivateNegotiation ? 300_000_000 : priceInCents + priceRange,
    priceMin: isPrivateNegotiation ? 0 : priceInCents - priceRange,
    roomsMax: rooms + 1,
    roomsMin: rooms - 1,
  }
}

export const calculateDeltaPrice = (similarInfo: DeltaPriceInfo, listingInfo: DeltaPriceInfo) =>
  similarInfo.isPrivateNegotiation || listingInfo.isPrivateNegotiation
    ? 10_000_000
    : Math.abs(similarInfo.sellingPriceCents - listingInfo.sellingPriceCents)

export const computeDelta = (similar: SimilarCriteria, listing: SimilarCriteria): number => {
  const { area: areaRange, price: priceRange } = getQueryRange(listing)
  const deltaArea = Math.abs(similar.commercialArea - listing.commercialArea)
  const deltaPrice = calculateDeltaPrice(
    {
      isPrivateNegotiation: similar.isPrivateNegotiation,
      sellingPriceCents: similar.priceInCents,
    },
    {
      isPrivateNegotiation: listing.isPrivateNegotiation,
      sellingPriceCents: listing.priceInCents,
    }
  )
  const deltaRooms = Math.abs(similar.rooms - listing.rooms)

  const deltaZone = similar.zoneSlug === listing.zoneSlug ? 0 : 1

  const areaFactor = 1 / areaRange
  const priceFactor = 1 / priceRange
  const weightArea = 2
  const weightPrice = 3
  const weightRooms = 4
  const weightZone = 5

  return (
    deltaArea * areaFactor * weightArea +
    deltaPrice * priceFactor * weightPrice +
    deltaRooms * weightRooms +
    deltaZone * weightZone
  )
}

export const getTopMostSimilar = (
  similarProperties: CardData[],
  property: SimilarCriteria,
  amount: number
): CardData[] =>
  similarProperties
    .filter(({ id }) => id !== property.id)
    .map((similar) => ({
      delta: computeDelta(
        {
          commercialArea: similar.commercialArea,
          id: similar.id,
          isPrivateNegotiation: similar?.isPrivateNegotiation || false,
          priceInCents: similar.price.cents,
          rooms: similar.rooms,
          zoneSlug: similar?.zone?.slug ?? null,
        },
        {
          commercialArea: property.commercialArea,
          id: property.id,
          isPrivateNegotiation: property.isPrivateNegotiation,
          priceInCents: property.priceInCents,
          rooms: property.rooms,
          zoneSlug: property?.zoneSlug ?? null,
        }
      ),
      similar: similar,
    }))
    .sort(({ delta: a }, { delta: b }) => a - b)
    .map(({ similar }) => similar)
    .slice(0, amount)

export const mapListingIntoSimilarCriteria = (listing: Listings_Detail): SimilarCriteria => {
  const priceInCents = listing.detail.sellingPrices.cents
  const commercialArea = listing.detail.commercialArea
  const rooms = listing.characteristics.rooms
  const isPrivateNegotiation = listing.detail.privateNegotiation
  const id = listing.id
  const zoneSlug = listing.detail.zone?.slug ? listing.detail.zone.slug : null
  const city = listing.detail.city

  return {
    city,
    commercialArea,
    id,
    isPrivateNegotiation,
    priceInCents,
    rooms,
    zoneSlug,
  }
}

export const mapPriorityIntoSimilarCriteria = (priority: Potential_Property): SimilarCriteria => {
  const priceInCents = priority.askedPrice.cents
  const commercialArea = priority.commercialArea
  const rooms = priority.rooms
  const isPrivateNegotiation = false
  const id = priority.transactionId
  const zoneSlug = priority.zone?.slug ? priority.zone.slug : null
  const city = priority.city

  return {
    city,
    commercialArea,
    id,
    isPrivateNegotiation,
    priceInCents,
    rooms,
    zoneSlug,
  }
}

export const fetchSimilarListingsFromListing = async (
  listing: Listings_Detail,
  locale: Locale,
  tenant: Tenants = TENANTS.casavo
) => {
  if (tenant === TENANTS.ucsc) return []
  const similarListings = await fetchListingBySimilar(similarRanges(mapListingIntoSimilarCriteria(listing), false))
  const similarListingsParsed = mapListingsIntoCardDataListing(similarListings, locale)
  const listingParsed = mapListingIntoSimilarCriteria(listing)
  return getTopMostSimilar(similarListingsParsed, listingParsed, MAX_SIMILAR_LISTINGS)
}

export const fetchSimilarListingsFromPriority = async (
  priority: Potential_Property,
  locale: Locale,
  tenant: Tenants = TENANTS.casavo
) => {
  if (tenant === TENANTS.ucsc) return []
  //TODO: check if geoSlugsIncludeCity should be true or false
  const similarListings = await fetchListingBySimilar(similarRanges(mapPriorityIntoSimilarCriteria(priority), true))
  const similarListingsParsed = mapListingsIntoCardDataListing(similarListings, locale)

  const priorityParsed = mapPriorityIntoSimilarCriteria(priority)

  return getTopMostSimilar(similarListingsParsed, priorityParsed, MAX_SIMILAR_LISTINGS)
}

export const fetchSimilarPrioritiesFromListing = async (listing: Listings_Detail) => {
  const similarPriorities = await fetchPrioritiesBySimilar(similarRanges(mapListingIntoSimilarCriteria(listing), true))
  const similarPrioritiesParsed = mapPrioritiesIntoCardDataPriority(similarPriorities)
  const listingParsed = mapListingIntoSimilarCriteria(listing)

  return getTopMostSimilar(similarPrioritiesParsed, listingParsed, MAX_SIMILAR_LISTINGS)
}

export const fetchSimilarPrioritiesFromPriority = async (
  priority: Potential_Property,
  tenant: Tenants = TENANTS.casavo
) => {
  if (tenant === TENANTS.ucsc) return []
  const similarPriorities = await fetchPrioritiesBySimilar(
    similarRanges(mapPriorityIntoSimilarCriteria(priority), true)
  )
  const similarPrioritiesParsed = mapPrioritiesIntoCardDataPriority(similarPriorities)

  const priorityParsed = mapPriorityIntoSimilarCriteria(priority)

  return getTopMostSimilar(similarPrioritiesParsed, priorityParsed, MAX_SIMILAR_LISTINGS)
}
